import React from 'react'
import Footer from 'components/Footer'
import DynamicHeader from 'components/DynamicHeader'
import { GameINFOV2, PartnerPageData } from 'types/types'
import Head from 'next/head'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import { Box, Grid, IconButton, Typography } from '@mui/material'
import { BASE_API_URL } from '@/configs'
import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'
import axios from 'axios'
import { useRouter } from 'next/router'
import { getPartnerIdentifier } from '@/utils/getPartnerIdentifier'
import { getStarSportsFontStyle } from '@/utils/starSportsUtils'
import { getCorrectMainFont } from '@/utils/getCorrectMainFont'
import { TEXT_DARK_COLORS_VARIANTS } from '@/utils/constants/colors'

const DEFAULT_PRIZE_IMAGE = '/tbl_logo_prizedraws.png'

function PrizePage({ page, game }: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const router = useRouter()

  const prizeHeader = game?.prize_page_header || page.prize_header

  const prizeDescription = game?.prize_page_description || page.prize_description

  const prizeImage = game?.prize_page_image || page.prize_image || DEFAULT_PRIZE_IMAGE
  const isDefaultPrizeImage = prizeImage === DEFAULT_PRIZE_IMAGE

  const handleGoBack = () => {
    if (game?.id) {
      router.push({
        pathname: `/${page.company_domain}/games/${game.id}`,
        query: { redirectBackToLanding: 'false' },
      })
    } else {
      router.back()
    }
  }

  return (
    <Box display="flex" justifyContent="space-between" flexDirection="column" height="100vh">
      <Head>
        <title>{page.site_title}</title>
        <meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1" />
        <link rel="icon" href={page.favicon} />
        <link rel="apple-touch-icon" href={page.favicon} type="image/png" />
        <link rel="shortcut icon" href={page.favicon} type="image/png" />
        {page?.metadata_image && <meta name="og:image" content={page?.metadata_image} />}
        <meta name="og:url" content={page?.company_domain} />
        <meta property="og:type" content="article" />
        <meta property="og:locale" content="en_GB" />
        <meta property="og:type" content="website" />
        <meta property="og:image:type" content="image/jpg" />
        <meta property="og:image:width" content="300" />
        <meta property="og:image:height" content="300" />
      </Head>
      <DynamicHeader
        pageTheme={{ ...page, is_user_joined: false }}
        shouldShowBox={false}
        shouldShowLeagueDropdown={false}
        game={game}
      />
      <Box display="flex" justifyContent="flex-start" ml={4} mb={2}>
        <IconButton onClick={handleGoBack} sx={{ width: 14, height: 14, mt: 2 }}>
          <ArrowBackIosIcon sx={{ width: 14, height: 14 }} />
        </IconButton>
      </Box>
      <Grid
        container
        sx={{
          margin: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          width: '100%',
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          flexDirection="column"
          justifyContent="space-between"
          sx={{ width: '100%' }}
        >
          <Typography
            mb="30px"
            fontWeight="900"
            fontStyle={getStarSportsFontStyle(page)}
            fontSize={24}
            color={page.game_page_second_header_color}
            fontFamily={getCorrectMainFont(page)}
            textAlign="center"
            px={2}
          >
            {prizeHeader}
          </Typography>
          <Box
            component="a"
            target="_blank"
            href={page?.links[0]?.url ?? ''}
            rel="noreferrer"
            display="flex"
            justifyContent="center"
            width="100%"
            maxWidth={{ xs: '90%', md: 480 }}
            mb="30px"
          >
            <Box
              component="img"
              src={prizeImage}
              alt="prize"
              width="100%"
              height="auto"
              sx={{
                objectFit: 'contain',
                // The default logo is white, so it needs a dark backdrop to be visible
                ...(isDefaultPrizeImage && {
                  bgcolor: TEXT_DARK_COLORS_VARIANTS[280],
                  borderRadius: '0.75rem',
                  p: 3,
                }),
              }}
            />
          </Box>
          <img
            // eslint-disable-next-line no-template-curly-in-string
            src="https://ad.doubleclick.net/ddm/trackimp/N1153799.3870138VPAR/B29145084.371022362;dc_trk_aid=561158848;dc_trk_cid=192856382;ord=[timestamp];dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;gdpr=${GDPR};gdpr_consent=${GDPR_CONSENT_755};ltd=;dc_tdv=1"
            style={{ display: 'none' }}
            width="1px"
            height="1px"
            alt="Impression Pixel"
          />
        </Box>
        <Box width="100%" maxWidth={672} mx="auto" px={2} pb={3}>
          <Box
            dangerouslySetInnerHTML={{
              __html: prizeDescription ?? '',
            }}
            sx={{
              textAlign: 'left',
              fontSize: '0.875rem',
              lineHeight: 1.6,
              color: page.base_game_text_color,
              fontFamily: page.fonts.secondary_font.family,
              '& p': { mt: 0, mb: 1.5 },
              '& ul, & ol': { mt: 0, mb: 1.5, pl: 3 },
              '& li': { mb: 0.5 },
              '& img': { maxWidth: '100%', height: 'auto' },
              // globals.css strips link styling, so admin-added links (e.g. T&Cs) read as plain text
              '& a': {
                textDecoration: 'underline',
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': { opacity: 0.8 },
              },
            }}
          />
        </Box>
      </Grid>
      <Footer page={page} />
    </Box>
  )
}

export default PrizePage

export const getServerSideProps = async (context: GetServerSidePropsContext) => {
  const { slug, identifier, redirect } = getPartnerIdentifier(context)
  if (redirect) {
    return { redirect }
  }

  const { gameId } = context.query
  const gameIdNumber = gameId && !Array.isArray(gameId) ? Number(gameId) : null

  const pagePromise = axios.get<PartnerPageData>(`${BASE_API_URL}/b2b/partner-pages/${identifier}`)
  const gamePromise =
    gameIdNumber && !Number.isNaN(gameIdNumber)
      ? axios.get<GameINFOV2>(`${BASE_API_URL}/games/v2/${gameIdNumber}`).catch(() => null)
      : Promise.resolve(null)

  const [res, gameRes] = await Promise.all([pagePromise, gamePromise])

  return {
    props: {
      page: res.data,
      game: gameRes?.data ?? null,
    },
  }
}
